import React, { useEffect, useMemo, useState } from "react";
import {
  Grid,
  Typography,
  IconButton as MuiIconButton,
  Divider,
  MenuItem,
  useTheme,
  useMediaQuery,
  Card,
  Box,
} from "@mui/material";
import BasicDatePicker from "../../../components/ui/BasicDatePicker";
import PaperCard from "../../../components/ui/PaperCard";
import ModalPopup from "../../../components/ui/ModalPopup";
import ViewIcon from "./../../../assets/images/icons/eye.svg";
import DownloadIcon from "./../../../assets/images/icons/download.svg";
import EmailIcon from "./../../../assets/images/icons/mail.svg";
import PDFViewer from "../../../components/ui/PDFViewer";
import PdfPasswordInfo from "../../../components/ui/PdfPasswordInfo";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../services/redux/store";
import dayjs from "dayjs"; // Import dayjs for date handling
import postsServices from "../../../services/api/service/postsService";
import {
  authSelector,
  userPermissionSelector,
  userBusinessSelector,
  userLoanCatSelector,
} from "../../../services/redux/slices/authSlice";
import Toast from "../../../components/ui/Toast";
import LoaderBackdrop from "../../../components/ui/LoaderBackdrop";
import { setStatusPopup } from "../../../services/redux/slices/statusPopupSlice";
import { documentsItOptions, messages } from "../../../data/constant";
import { InputField } from "../../../components/ui/Inputs";
import { dateToFinYearOptions } from "../../../utils/helpers";
import { decrypt } from "../../../utils/crypto";
import HeadingLeftRight from "../../../components/ui/HeadingLeftRight";
import { styled } from "@mui/material/styles";

interface RetrievedData {
  cifNumber?: string | null;
}

interface Payload {
  finReference: string;
  fromDate: string | null;
  toDate: string | null;
  type: string;
  cif: string;
  action: string;
}

export default function Statement() {
  const [viewPdfModal, setViewPdfModal] = useState<{
    isOpen: boolean;
    title: string;
  }>({ isOpen: false, title: "" });
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [pdfUrlAccountStatement, setPdfUrlAccountStatement] =
    useState<string>("");
  const [lanNumber, setLanNumber] = useState("");
  const [toDate, setToDate] = useState(new Date());
  const { userProfile } = useSelector(authSelector);
  const { rolePermissions } = useSelector(userPermissionSelector);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const [minDate, setMinDate] = useState<Date | null>(null);
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("sm"));
  const IconButton = styled("div")({
    color: "rgba(75, 70, 92, 1)",
    fontSize: "15px",
    display: "flex",
    float: "right",
  });
  const Typography = styled("div")({
    color: "#00b3b5",
    fontWeight: "500",
    marginLeft: "8px",
    marginRight: "5px",
    fontSize: "clamp(12px, 1.2vw, 13px)",
  });

  const selectedLoanNumber = useSelector(
    (state: RootState) => state.dashboardData.selectedLoanNumber
  );

  const dashboardDataSet = useSelector(
    (state: RootState) => state.dashboardData.sanctionedDisbursedData
  );

  const loanAccountNumberOptions = useSelector(
    (state: RootState) => state.option.loanAccountNumberOptions
  );

  const selectedOption = useSelector(
    (state: RootState) => state.option.selectedOption
  );

  const userBusinessSelected = useSelector(userBusinessSelector);
  const userLoanCategory = useSelector(userLoanCatSelector);

  useEffect(() => {
    const matchedObject = dashboardDataSet.find(
      (data: any) => data.loanAccountNumber === selectedLoanNumber
    );
    const disbDate = matchedObject?.beneficiaryName[0]?.disbDate ?? null;
    if (disbDate) {
      const parsedDate = new Date(disbDate);
      setFromDate(parsedDate);
      setLanNumber(selectedLoanNumber);
      if (!minDate) {
        setMinDate(parsedDate);
      }
    }
  }, [selectedLoanNumber]);

  const accountStatement = async (actionType: string, type: string) => {
    setIsLoading(true);
    try {
      const formattedFromDate = fromDate
        ? dayjs(fromDate).format("YYYY-MM-DD")
        : null;
      const formattedToDate = toDate
        ? dayjs(toDate).format("YYYY-MM-DD")
        : null;

      const payload: any = {
        finReference: selectedLoanNumber,
        fromDate: formattedFromDate,
        toDate: formattedToDate,
        type: type,
        cif: decrypt(userProfile?.cifNumber),
        action: actionType,
        loanCat: "flexi",
      };

      const res = await postsServices.addPost(
        "api/v1/report/accountstatement",
        payload
      );

      setIsLoading(false);
      if (res?.status === 200 && res?.data?.data) {
        const pdfUrl = res?.data?.data?.url;
        if (type === "soa") {
          // SOA
          if (actionType === "view") {
            viewPdf(pdfUrl);
            if(!isXsOrSm){
              setViewPdfModal({ isOpen: true, title: "Account Statement" });
            }
          } else if (actionType === "download") {
            downloadPdf(pdfUrl, lanNumber, type);
            dispatch(
              setStatusPopup({
                status: "success",
                message: `Your Account Statement for LAN ${selectedLoanNumber} has been downloaded successfully.`,
              })
            );
          } else if (actionType === "email") {
            dispatch(
              setStatusPopup({
                status: "success",
                message: `Statement of Account has been sent successfully to your registered Email ID`,
              })
            );
          }
        } else if (type === "schd") {
          // schd
          if (actionType === "view") {
            viewPdf(pdfUrl);
            if(!isXsOrSm){
              setViewPdfModal({ isOpen: true, title: "Repayment Schedule" });
            }
          } else if (actionType === "download") {
            downloadPdf(pdfUrl, lanNumber, type);
            dispatch(
              setStatusPopup({
                status: "success",
                message: `Your Repayment Statement schedule for LAN ${selectedLoanNumber} has been downloaded successfully.`,
              })
            );
          } else if (actionType === "email") {
            dispatch(
              setStatusPopup({
                status: "success",
                message: `Repayment Schedule has been sent successfully to your registered Email ID`,
              })
            );
          }
        }
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "An error occurred";
      dispatch(setStatusPopup({ status: "error", message: errorMessage }));
      setIsLoading(false);
    }
  };

  const welcomeLetter = async (actionType: string) => {
    try {
      const payload = {
        lan: selectedLoanNumber,
        docCategory: "WELCOMELETTER",
        cif: decrypt(userProfile?.cifNumber),
        action: actionType,
        loanCat: "flexi",
      };
      const res = await postsServices.addPost(
        "api/v1/report/welcomeletter",
        payload
      );
      if (res?.status === 200 && res?.data?.data) {
        const pdfUrl = res?.data?.data?.url;
        if (actionType === "view") {
          viewPdf(pdfUrl);
          if(!isXsOrSm){
            setViewPdfModal({ isOpen: true, title: "Welcome letter" });
          }
        } else if (actionType === "download") {
          downloadPdf(pdfUrl, lanNumber, "wc");
          dispatch(
            setStatusPopup({
              status: "success",
              message: `Your welcome letter has been downloaded successfully.`,
            })
          );
        } else if (actionType === "email") {
          dispatch(
            setStatusPopup({
              status: "success",
              message: `Welcome letter has been sent successfully to your registered Email ID`,
            })
          );
        }
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "An error occurred";
      dispatch(setStatusPopup({ status: "error", message: errorMessage }));
    }
  };

  const handleStatementAction = (action: string, type: string) => {
    if (!type.includes(`wc`)) {
      const selectedLoanCategoryObj: any = userLoanCategory?.Flexi?.find(
        (loan: any) => loan?.businessId === userBusinessSelected?.id
      );

      if (
        rolePermissions?.includes(`statement_${action}`) ||
        selectedLoanCategoryObj?.permissionBypass
      ) {
        accountStatement(action, type);
      } else {
        dispatch(
          setStatusPopup({
            status: "no access",
            message: `${messages.noPermission.action} ${action}`,
          })
        );
      }
    } else {
      welcomeLetter(action);
    }
  };

  const viewPdf = async (pdfUrl: string) => {
    try {
      const httpsUrl = pdfUrl.replace(/^http:\/\//i, "https://");
      const response = await fetch(httpsUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch PDF: ${response.statusText}`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      setPdfUrlAccountStatement(pdfUrl);

      if (isXsOrSm) {
        //setViewPdfModal((prev) => ({ ...prev, isOpen: true }));
        window.open(url, "_blank");
        setIsLoading(false);
      } else {
        setPdfUrlAccountStatement(pdfUrl);
        setViewPdfModal((prev) => ({ ...prev, isOpen: true }));
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      console.error("Error sending email:", error);
    }
  };

  const downloadPdf = async (pdfUrl: string, lan: string, type: string) => {
    try {
      const httpsUrl = pdfUrl.replace(/^http:\/\//i, "https://");
      const response = await fetch(httpsUrl, {
        method: "GET",
        mode: "cors",
        referrerPolicy: "no-referrer",
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to fetch PDF: ${response.statusText} - ${errorText}`
        );
      }

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/pdf")) {
        const errorText = await response.text();
        throw new Error(
          `The fetched file is not a PDF. Response: ${errorText}`
        );
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.target = "_blank";
      anchor.download = `${lan}_${type}.pdf`;

      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);

      window.URL.revokeObjectURL(url);
      console.log("PDF downloaded successfully.");
    } catch (error) {
      console.error("Error downloading PDF:", error);
      // Handle error state or display error message
    } finally {
      setIsLoading(false);
    }
  };
  const handleDateChange = (date: Date | null) => {
    if (date !== null) {
      setToDate(date); // Update toDate state when date is not null
    }
    // Optionally handle the case when date is null, if needed
  };
  const handleFromChange = (date: Date | null) => {
    if (date !== null) {
      setFromDate(date);
    }
  };

  return (
    <Box>
      <Grid container style={{ padding: "0px" }}>
        {!isXsOrSm && (
          <Grid item xs={0} sm={0} md={4} lg={4}>
            <HeadingLeftRight titleone="Documents" displayC="true" />
          </Grid>
        )}

        <Grid item xs={12} sm={12} md={12} lg={12}>
          {isXsOrSm && (
            <HeadingLeftRight titleone="Documents" displayC="true" />
          )}
          <Grid container spacing={1}>
            <Grid item xs={12} sm={0} md={8} lg={8}></Grid>
            <Grid item xs={6} sm={6} md={2} lg={2}>
              {fromDate && (
                <BasicDatePicker
                  label="From Date"
                  width="100%"
                  value={fromDate ? dayjs(fromDate) : null}
                  onChange={handleFromChange}
                  minDate={minDate ? dayjs(minDate) : undefined}
                />
              )}
            </Grid>
            <Grid item xs={6} sm={6} md={2} lg={2}>
              <BasicDatePicker
                label="To Date"
                width="100%"
                onChange={handleDateChange}
                value={toDate ? dayjs(toDate) : null}
                minDate={minDate ? dayjs(minDate) : undefined}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Card
        sx={{
          ...(isXsOrSm && {
            background: "#F8F7FA",
            borderWidth: "0px",
            borderRadius: "0px",
            boxShadow: "none",
          }),
        }}
      >
        <Grid
          container
          spacing={1}
          className={`statement-container ${isXsOrSm ? "statement-container-mobile" : ""
            }`}
        >
          <Grid item xs={12} sm={12} md={12} lg={12}>
            <Grid
              container
              className={`sc-row ${isXsOrSm ? "sc-row-mobile" : ""}`}
              sx={{ marginBottom: "20px" }}
            >
              <Grid xs={12} sm={12} md={7} lg={7}>
                <Typography className="sm-col-1">
                  {/* Account Statement */}
                  Statement Of Account
                </Typography>
              </Grid>
              {isXsOrSm && (
                <Grid item xs={12} sm={12} md={7} lg={7}>
                  <Divider sx={{ marginBottom: "10px", marginTop: "10px" }} />
                </Grid>
              )}
              <Grid item xs={12} sm={12} md={5} lg={5}>
                <Grid container>
                  <Grid item xs={4} sm={4} md={4} lg={4} className="icon-lable">
                    <IconButton
                      onClick={() => handleStatementAction("view", "soa")}
                    >
                      <img src={ViewIcon} alt="View Icon" />
                      <Typography>View</Typography>
                    </IconButton>
                  </Grid>
                  <Grid item xs={4} sm={4} md={4} lg={4} className="icon-lable">
                    <IconButton
                      onClick={() => handleStatementAction("download", "soa")}
                    >
                      <img src={DownloadIcon} alt="Download Icon" />
                      <Typography>Download</Typography>
                    </IconButton>
                  </Grid>
                  <Grid item xs={4} sm={4} md={4} lg={4} className="icon-lable">
                    <Box sx={{ float: "left", marginLeft: "29px" }}>
                      <IconButton
                        onClick={() => handleStatementAction("email", "soa")}
                      >
                        <img
                          src={EmailIcon}
                          alt="Email Icon"
                          className="pointer"
                        />
                        <Typography className="pointer">
                          Email
                        </Typography>
                      </IconButton>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid
              container
              className={`sc-row ${isXsOrSm ? "sc-row-mobile" : ""}`}
            >
              <Grid item xs={12} sm={12} md={7} lg={7}>
                <Typography className="sm-col-1">
                  Repayment Schedule
                </Typography>
              </Grid>
              {isXsOrSm && (
                <Grid item xs={12} sm={12} md={7} lg={7}>
                  <Divider sx={{ marginBottom: "10px", marginTop: "10px" }} />
                </Grid>
              )}
              <Grid item xs={12} sm={12} md={5} lg={5}>
                <Grid container>
                  <Grid item xs={4} sm={4} md={4} lg={4} className="icon-lable">
                    {" "}
                    <IconButton
                      onClick={() => handleStatementAction("view", "schd")}
                    >
                      <img src={ViewIcon} alt="View Icon" />
                      <Typography>View</Typography>
                    </IconButton>
                  </Grid>
                  <Grid item xs={4} sm={4} md={4} lg={4} className="icon-lable">
                    {" "}
                    <IconButton
                      onClick={() => handleStatementAction("download", "schd")}
                    >
                      <img src={DownloadIcon} alt="Download Icon" />
                      <Typography>Download</Typography>
                    </IconButton>
                  </Grid>
                  <Grid item xs={4} sm={4} md={4} lg={4} className="icon-lable">
                    <Box sx={{ float: "left", marginLeft: "29px" }}>
                      <IconButton
                        sx={{ display: "flex", alignItems: "center" }}
                        onClick={() => handleStatementAction("email", "schd")}
                      >
                        <img
                          src={EmailIcon}
                          alt="Email Icon"
                          className="pointer"
                        />
                        <Typography className="pointer">
                          Email
                        </Typography>
                      </IconButton>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid
              container
              className={`sc-row ${isXsOrSm ? "sc-row-mobile" : ""}`}
            >
              <Grid item xs={12} sm={12} md={7} lg={7}>
                <Typography className="sm-col-1">Welcome Letter</Typography>
              </Grid>
              {isXsOrSm && (
                <Grid item xs={12} sm={12} md={7} lg={7}>
                  <Divider sx={{ marginBottom: "10px", marginTop: "10px" }} />
                </Grid>
              )}
              <Grid item xs={12} sm={12} md={5} lg={5}>
                <Grid container>
                  <Grid item xs={4} sm={4} md={4} lg={4} className="icon-lable">
                    {" "}
                    <IconButton
                      onClick={() => handleStatementAction("view", "wc")}
                    >
                      <img src={ViewIcon} alt="View Icon" />
                      <Typography>View</Typography>
                    </IconButton>
                  </Grid>
                  <Grid item xs={4} sm={4} md={4} lg={4} className="icon-lable">
                    <IconButton
                      onClick={() => handleStatementAction("download", "wc")}
                    >
                      <img src={DownloadIcon} alt="Download Icon" />
                      <Typography>Download</Typography>
                    </IconButton>
                  </Grid>
                  <Grid item xs={4} sm={4} md={4} lg={4} className="icon-lable">
                    <Box sx={{ float: "left", marginLeft: "29px" }}>
                      {" "}
                      <IconButton
                        onClick={() => handleStatementAction("email", "wc")}
                      >
                        <img
                          src={EmailIcon}
                          alt="Email Icon"
                          className="pointer"
                        />
                        <Typography className="pointer">
                          Email
                        </Typography>
                      </IconButton>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <PdfPasswordInfo />
          </Grid>
        </Grid>
      </Card>

      <ModalPopup
        isOpen={viewPdfModal?.isOpen}
        onClose={() => setViewPdfModal({ ...viewPdfModal, isOpen: false })}
        title={viewPdfModal?.title}
        style={{ height: "100%", overflowX: "hidden", minWidth: "70%" }}
        onNavigate={() => {
          throw new Error("Function not implemented.");
        }}
      >
        <Divider sx={{ marginBottom: "10px", marginTop: "10px" }} />
        <PDFViewer pdfUrl={pdfUrlAccountStatement} />
      </ModalPopup>

      <LoaderBackdrop open={isLoading} />
    </Box>
  );
}
